import dayjs from 'dayjs';
import { normalizeFileName } from '../../../components/ui/utils/fileNameEncoding';

const ENTITY_TYPE_ID = 6;

const TOPIC_LABELS = {
  AUTHORITIES: '1. Authorities',
  ASSESSMENT_PROCEDURES: '2. Assessment procedures',
  PROCEDURES: '3. Procedures',
  CALIBRATIONS: '4. Calibrations',
  MAINTENANCE: '5. Maintenance',
  TRAINING: '6. Training',
  SAFETY: '7. Safety',
  VALIDATION: '8. Validation',
  SUPPLIERS: '9. Suppliers',
  NEW_PRODUCT: '10. New product',
  NEW_FACILITY: '11. New facility',
  DISTRIBUTION: '12. Distribution',
  NEW_CUSTOMER: '13. New customer',
  QA: '14. QA',
  OTHER: 'Other',
};

const kv = (label, value) => [
  {
    text: label,
    style: 'labelCell',
    border: [true, true, false, true],
    fillColor: '#f8fafc',
  },
  {
    text: value == null || value === '' ? '—' : String(value),
    style: 'valueCell',
    border: [false, true, true, true],
  },
];

const sectionTitle = (title) => ({
  text: title,
  style: 'sectionTitle',
  margin: [0, 14, 0, 6],
});

const userName = (user) =>
  user?.userDisplayName ||
  [user?.userFirstName, user?.userLastName].filter(Boolean).join(' ') ||
  '';

/**
 * Export Change Request / Change Control (ΕΝ 01-ΓΔ.14) to PDF.
 * When includeAttachments is true, downloads a ZIP with the PDF + files.
 */
export const exportChangeRequestToPdf = async (
  record,
  includeAttachments = false,
) => {
  let missingFilesCount = 0;

  try {
    const pdfMake = await import('pdfmake/build/pdfmake');
    const pdfFonts = await import('pdfmake/build/vfs_fonts');
    const { API_BASE_URL, API_ENDPOINTS } = await import(
      '../../../config/apiConfig'
    );

    const vfs = pdfFonts.vfs || pdfFonts.default?.vfs || pdfFonts.default || {};
    const logoImageKey = 'companyLogo';
    let companyLogoBase64 = null;

    try {
      const token = localStorage.getItem('authToken');
      const logoSettingResponse = await fetch(
        `${API_BASE_URL}/application-setting/key/companyLogo`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      if (logoSettingResponse.ok) {
        const logoSetting = await logoSettingResponse.json();
        if (logoSetting?.value && logoSetting.isActive !== false) {
          let logoUrl = logoSetting.value.trim();
          if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
            const baseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
            logoUrl = logoUrl.startsWith('/')
              ? `${baseUrl}${logoUrl}`
              : `${baseUrl}/${logoUrl}`;
          }
          const imageResponse = await fetch(logoUrl);
          if (imageResponse.ok) {
            const blob = await imageResponse.blob();
            companyLogoBase64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
          }
        }
      }
    } catch (error) {
      console.warn('Could not load company logo:', error);
    }

    const fetchFileAsBase64 = async (fileId, fileName) => {
      try {
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        const response = await fetch(
          API_ENDPOINTS.fileAttachment.download(fileId),
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...(userId ? { 'user-id': userId } : {}),
            },
          },
        );
        if (!response.ok) return null;
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              data: reader.result.split(',')[1],
              fileName,
              mimeType: blob.type || 'application/octet-stream',
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    };

    const fileAttachmentsData = [];
    let listedAttachments = Array.isArray(record.fileAttachments)
      ? record.fileAttachments.filter((f) => !f.isDeleted)
      : [];

    if (record?.id) {
      try {
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        const filesResponse = await fetch(
          `${API_BASE_URL}/file-attachment/entity/${ENTITY_TYPE_ID}/${record.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...(userId ? { 'user-id': userId } : {}),
            },
          },
        );
        if (filesResponse.ok) {
          const files = await filesResponse.json();
          listedAttachments = (Array.isArray(files) ? files : []).filter(
            (f) => !f.isDeleted,
          );
        }
      } catch (error) {
        console.warn('Error listing file attachments for PDF:', error);
      }
    }

    if (includeAttachments && listedAttachments.length > 0) {
      for (const file of listedAttachments) {
        const fileData = await fetchFileAsBase64(
          file.id,
          normalizeFileName(file.fileName || ''),
        );
        if (fileData) {
          fileAttachmentsData.push({ ...fileData, originalFile: file });
        } else {
          missingFilesCount += 1;
        }
      }
    }

    const formatDate = (date) => {
      if (!date) return '';
      try {
        return dayjs(date).format('DD/MM/YYYY');
      } catch {
        return '';
      }
    };

    const formatDateTime = (date) => {
      if (!date) return '';
      try {
        return dayjs(date).format('DD/MM/YYYY HH:mm');
      } catch {
        return '';
      }
    };

    const topics = (Array.isArray(record.topicCodes) ? record.topicCodes : [])
      .map((c) => TOPIC_LABELS[c] || c)
      .join(', ');

    const copyTargets = [
      record.copyToQa && 'QA',
      record.copyToSafety && 'Safety Officer',
      record.copyToCustomer && 'Customer',
      record.copyToWarehouse && 'Warehouse',
      record.copyToCommercial && 'Commercial',
      record.copyToOther &&
        (record.copyOtherNote
          ? `Other (${record.copyOtherNote})`
          : 'Other'),
    ]
      .filter(Boolean)
      .join(', ');

    const actions = Array.isArray(record.actions) ? record.actions : [];
    const actionRows =
      actions.length > 0
        ? [
            [
              { text: 'Action', style: 'tableHeader' },
              { text: 'Responsible', style: 'tableHeader' },
              { text: 'Due date', style: 'tableHeader' },
            ],
            ...actions.map((a) => {
              let responsible = '—';
              if (a.responsibleType === 'ROLE' || a.responsibleRole) {
                responsible = `Group: ${a.responsibleRole?.name || a.responsibleRoleId || '—'}`;
              } else if (a.responsibleUser || a.responsibleUserId) {
                responsible = `User: ${userName(a.responsibleUser) || a.responsibleUserId || '—'}`;
              }
              return [
                { text: a.description || '—', style: 'tableCell' },
                { text: responsible, style: 'tableCell' },
                { text: formatDate(a.dueDate) || '—', style: 'tableCell' },
              ];
            }),
          ]
        : [
            [
              {
                text: 'No actions recorded',
                colSpan: 3,
                style: 'tableCell',
                alignment: 'center',
                color: '#6b7280',
              },
              {},
              {},
            ],
          ];

    const content = [
      ...(companyLogoBase64
        ? [
            {
              image: logoImageKey,
              width: 140,
              alignment: 'left',
              margin: [0, 0, 0, 10],
            },
          ]
        : []),
      {
        text: 'CHANGE REQUEST / CHANGE CONTROL',
        style: 'docTitle',
        margin: [0, 0, 0, 2],
      },
      {
        text: record.referenceDocument || 'ΕΝ 01-ΓΔ.14/8',
        style: 'docSubtitle',
        margin: [0, 0, 0, 12],
      },
      {
        table: {
          widths: [140, '*'],
          body: [
            kv('Code', record.code),
            kv('Status', record.status?.name || record.statusId || ''),
            kv('Initiated By', userName(record.initiatedBy)),
            kv('Initiation Date', formatDate(record.initiatedAt)),
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#cbd5e1',
          vLineColor: () => '#cbd5e1',
        },
        margin: [0, 0, 0, 8],
      },

      sectionTitle('I. Proposed Change'),
      {
        table: {
          widths: [140, '*'],
          body: [
            kv('Change Description', record.changeDescription),
            kv('Cause of Change', record.changeCause),
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#cbd5e1',
          vLineColor: () => '#cbd5e1',
        },
      },

      sectionTitle('II. Investigation'),
      {
        table: {
          widths: [140, '*'],
          body: [
            kv('Topics', topics || (record.topicOther ? `Other: ${record.topicOther}` : '')),
            ...(record.topicOther && topics
              ? [kv('Other (details)', record.topicOther)]
              : []),
            kv(
              'QA Approval',
              [
                userName(record.qaInvestigationApprovedBy),
                formatDate(record.qaInvestigationApprovedAt),
              ]
                .filter(Boolean)
                .join(' — '),
            ),
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#cbd5e1',
          vLineColor: () => '#cbd5e1',
        },
      },

      sectionTitle('Actions Required'),
      {
        table: {
          widths: ['*', 140, 80],
          body: actionRows,
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#cbd5e1',
          vLineColor: () => '#cbd5e1',
          fillColor: (rowIndex) => (rowIndex === 0 ? '#f1f5f9' : null),
        },
      },

      sectionTitle('Classification & Approvals'),
      {
        table: {
          widths: [140, '*'],
          body: [
            kv('Classification', record.classification),
            kv(
              'Risk Assessment Required',
              record.riskAssessmentRequired ? 'Yes' : 'No',
            ),
            kv(
              'Risk Assessment (QRM)',
              record.riskAssessment?.code ||
                record.riskAssessmentId ||
                '',
            ),
            kv('Copy', copyTargets),
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#cbd5e1',
          vLineColor: () => '#cbd5e1',
        },
      },

      sectionTitle('III. Completion'),
      {
        table: {
          widths: [140, '*'],
          body: [
            kv('Extension Date', formatDate(record.extensionDate)),
            kv(
              'Extension QA Approval',
              [
                userName(record.extensionQaApprovedBy),
                formatDate(record.extensionQaApprovedAt),
              ]
                .filter(Boolean)
                .join(' — '),
            ),
            kv('Implementation Date', formatDate(record.implementationDate)),
            kv('Assessment Summary', record.assessmentSummary),
            kv('Actions Completed', record.actionsCompleted),
            kv('Actions Justification', record.actionsCompletedNote),
            kv(
              'Completion QA Approval',
              [
                userName(record.completionQaApprovedBy),
                formatDate(record.completionQaApprovedAt),
              ]
                .filter(Boolean)
                .join(' — '),
            ),
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#cbd5e1',
          vLineColor: () => '#cbd5e1',
        },
      },

      sectionTitle('IV. Effectiveness Check'),
      {
        table: {
          widths: [140, '*'],
          body: [
            kv(
              'Effectiveness 3m',
              [
                userName(record.effectiveness3mBy),
                formatDate(record.effectiveness3mAt),
              ]
                .filter(Boolean)
                .join(' — '),
            ),
            kv(
              'Effectiveness 6m',
              [
                userName(record.effectiveness6mBy),
                formatDate(record.effectiveness6mAt),
              ]
                .filter(Boolean)
                .join(' — '),
            ),
            kv('Effectiveness Comments', record.effectivenessComments),
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#cbd5e1',
          vLineColor: () => '#cbd5e1',
        },
      },

      {
        text: `Reference Document: ${record.referenceDocument || '—'}`,
        style: 'footerNote',
        margin: [0, 16, 0, 0],
      },
    ];

    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 50, 40, 50],
      defaultStyle: {
        font: 'Roboto',
        fontSize: 10,
        lineHeight: 1.35,
        color: '#111827',
      },
      header: (currentPage) => {
        if (companyLogoBase64 && currentPage > 1) {
          return {
            image: logoImageKey,
            width: 100,
            alignment: 'left',
            margin: [40, 16, 0, 0],
          };
        }
        return null;
      },
      footer: (currentPage, pageCount) => ({
        text: `Page ${currentPage} of ${pageCount} | Generated: ${formatDateTime(new Date())}`,
        alignment: 'center',
        fontSize: 8,
        color: '#9ca3af',
        margin: [0, 10, 0, 0],
      }),
      content,
      images: companyLogoBase64 ? { [logoImageKey]: companyLogoBase64 } : {},
      styles: {
        docTitle: { fontSize: 16, bold: true, alignment: 'center' },
        docSubtitle: {
          fontSize: 10,
          alignment: 'center',
          color: '#64748b',
        },
        sectionTitle: {
          fontSize: 11,
          bold: true,
          color: '#0f172a',
        },
        labelCell: { fontSize: 9, bold: true, color: '#334155' },
        valueCell: { fontSize: 10, color: '#111827' },
        tableHeader: { fontSize: 9, bold: true, color: '#334155' },
        tableCell: { fontSize: 9, color: '#111827' },
        footerNote: { fontSize: 9, color: '#64748b', italics: true },
      },
    };

    const pdfMakeApi = pdfMake.default || pdfMake;
    const pdfDoc = pdfMakeApi.createPdf(
      docDefinition,
      undefined,
      undefined,
      vfs,
    );
    const baseName = `Change-Request-${record?.code || record?.id || 'report'}`
      .replace(/[^\w.-]+/g, '_');

    if (includeAttachments && fileAttachmentsData.length > 0) {
      try {
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        pdfDoc.getBlob((pdfBlob) => {
          zip.file(`${baseName}.pdf`, pdfBlob);
          fileAttachmentsData.forEach((fileData) => {
            const binaryString = atob(fileData.data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i += 1) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            zip.file(fileData.fileName, bytes);
          });
          zip.generateAsync({ type: 'blob' }).then((zipBlob) => {
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${baseName}-with-attachments.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          });
        });
      } catch (error) {
        console.error('Error creating ZIP:', error);
        pdfDoc.download(`${baseName}.pdf`);
      }
    } else {
      pdfDoc.download(`${baseName}.pdf`);
    }

    return { success: true, missingFilesCount };
  } catch (error) {
    console.error('Error generating Change Request PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
};
